/**
 * Super-Admin — Biomarker Knowledge Base management
 *
 * • Download current KB (JSON or CSV)
 * • Upload a modified file (validate → diff preview → apply)
 * • Version history + revert
 * • Restore-to-default
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  BookOpen, Download, Upload, RotateCcw, History,
  CheckCircle2, XCircle, AlertTriangle, Loader2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

import { DashboardLayout } from "@/components/Layout";
import { api, API_BASE, apiError, getToken } from "@/lib/api";

const AUTH_HEADER = () => ({ Authorization: `Bearer ${getToken()}` });

/* ─────────────────────── helpers ─────────────────────── */

async function downloadBlob(url, filename) {
  const res = await fetch(url, { headers: AUTH_HEADER() });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

/* ─────────────────────── Current KB card ─────────────────────── */

function CurrentKbCard({ meta, onDownload }) {
  if (!meta) return null;
  const av = meta.active_version;
  return (
    <Card className="p-5 rounded-2xl" data-testid="kb-current-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <BookOpen className="w-5 h-5 text-efgh-orange" />
            <h2 className="font-heading font-semibold text-xl text-ink">Active knowledge base</h2>
            {meta.using_defaults ? (
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">Built-in defaults</span>
            ) : (
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Custom v{av?.version_number}</span>
            )}
          </div>
          <p className="text-ink/60 text-sm">
            <b className="text-ink" data-testid="kb-total-count">{meta.total}</b> biomarkers across <b>{Object.keys(meta.panels || {}).length}</b> panels.
            {av && (
              <> Last updated by <b>{av.uploaded_by}</b> on {fmtDate(av.uploaded_at)}.</>
            )}
          </p>
          {av?.notes && (
            <p className="text-ink/50 text-xs mt-1 italic">Notes: {av.notes}</p>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onDownload("json")} data-testid="kb-download-json">
            <Download className="w-4 h-4 mr-1.5" /> Download JSON
          </Button>
          <Button variant="outline" onClick={() => onDownload("csv")} data-testid="kb-download-csv">
            <Download className="w-4 h-4 mr-1.5" /> Download CSV
          </Button>
        </div>
      </div>
      {/* Panel breakdown */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
        {Object.entries(meta.panels || {}).sort((a, b) => b[1] - a[1]).map(([panel, count]) => (
          <div key={panel} className="text-xs flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-2.5 py-1.5">
            <span className="text-ink/70 truncate">{panel}</span>
            <span className="font-semibold text-ink ml-2 shrink-0">{count}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ─────────────────────── Upload card ─────────────────────── */

function UploadCard({ onValidated, onApplied, onRestore }) {
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [validation, setValidation] = useState(null); // { ok, errors, warnings, diff, parsed_count }
  const [applyBusy, setApplyBusy] = useState(false);
  const inputRef = React.useRef(null);

  const pickFile = (f) => {
    setFile(f);
    setValidation(null);
  };

  const validate = async () => {
    if (!file) return toast.error("Please select a file first");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/admin/knowledge-base/validate", form, { headers: { "Content-Type": "multipart/form-data" } });
      setValidation(r.data);
      if (r.data.ok) {
        toast.success(`Validated · ${r.data.parsed_count} biomarkers · ${r.data.diff.added.length} added / ${r.data.diff.removed.length} removed / ${r.data.diff.modified.length} modified`);
      } else {
        toast.error(`Validation found ${r.data.errors.length} error(s)`);
      }
      onValidated && onValidated(r.data);
    } catch (e) { toast.error(apiError(e, "Validation failed")); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    if (!file) return;
    if (!validation?.ok) return toast.error("Please validate first");
    if (!window.confirm(`Apply this knowledge base? It will replace the current active KB. Adds ${validation.diff.added.length}, removes ${validation.diff.removed.length}, modifies ${validation.diff.modified.length}.`)) return;
    setApplyBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("notes", notes);
      const r = await api.post("/admin/knowledge-base/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Applied · new version v${r.data.version.version_number}`);
      setFile(null); setValidation(null); setNotes("");
      if (inputRef.current) inputRef.current.value = "";
      onApplied && onApplied();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.errors) {
        toast.error(`${detail.message}: ${detail.errors[0]}`);
      } else { toast.error(apiError(e, "Upload failed")); }
    }
    finally { setApplyBusy(false); }
  };

  const restore = async () => {
    if (!window.confirm("Reset to the built-in default knowledge base? This creates a new version pointing at the shipped defaults — nothing is destructively lost.")) return;
    try {
      const r = await api.post("/admin/knowledge-base/restore-default");
      toast.success(`Restored defaults · v${r.data.version.version_number}`);
      onRestore && onRestore();
    } catch (e) { toast.error(apiError(e, "Restore failed")); }
  };

  return (
    <Card className="p-5 rounded-2xl" data-testid="kb-upload-card">
      <div className="flex items-center gap-2 mb-3">
        <Upload className="w-5 h-5 text-efgh-orange" />
        <h2 className="font-heading font-semibold text-xl text-ink">Upload updated knowledge base</h2>
      </div>
      <p className="text-ink/60 text-sm mb-4">
        Download the current file, hand it off to your expert clinicians (JSON is exact; CSV opens in Excel).
        Upload the edited file below. The system validates and shows you exactly what will change before you apply it.
      </p>

      <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
        <div>
          <Label htmlFor="kb-file" className="text-xs mb-1 block">File (.json or .csv, max 5&nbsp;MB)</Label>
          <input
            ref={inputRef}
            id="kb-file"
            type="file"
            accept=".json,.csv,application/json,text/csv"
            onChange={(e) => pickFile(e.target.files?.[0] || null)}
            data-testid="kb-file-input"
            className="text-sm w-full border border-input rounded-lg p-2 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:bg-efgh-orange/10 file:text-efgh-orange"
          />
          {file && <p className="text-ink/60 text-xs mt-1">Selected: <b>{file.name}</b> · {(file.size / 1024).toFixed(1)} KB</p>}
        </div>
        <Button onClick={validate} disabled={!file || busy} className="bg-slate-900 hover:bg-slate-800 text-white h-10" data-testid="kb-validate-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
          Validate
        </Button>
      </div>

      {validation && (
        <div className="mt-4 space-y-3">
          {validation.ok ? (
            <div className="p-3 rounded-xl border border-emerald-200 bg-emerald-50" data-testid="kb-validation-ok">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span className="font-semibold text-emerald-800">Validation passed · {validation.parsed_count} biomarkers</span>
              </div>
              <DiffPanel diff={validation.diff} />
            </div>
          ) : (
            <div className="p-3 rounded-xl border border-red-200 bg-red-50" data-testid="kb-validation-failed">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="w-4 h-4 text-red-600" />
                <span className="font-semibold text-red-800">Validation failed · {validation.errors.length} error(s)</span>
              </div>
              <ul className="text-sm text-red-700 list-disc pl-5 space-y-0.5 max-h-40 overflow-auto">
                {validation.errors.slice(0, 30).map((e, i) => <li key={i}>{e}</li>)}
                {validation.errors.length > 30 && <li className="italic">…and {validation.errors.length - 30} more</li>}
              </ul>
            </div>
          )}

          {validation.warnings?.length > 0 && (
            <div className="p-3 rounded-xl border border-amber-200 bg-amber-50">
              <div className="flex items-center gap-2 mb-1"><AlertTriangle className="w-4 h-4 text-amber-600" /><span className="font-semibold text-amber-800">Warnings</span></div>
              <ul className="text-xs text-amber-700 list-disc pl-5">
                {validation.warnings.slice(0, 10).map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {validation.ok && (
            <div className="space-y-2">
              <div>
                <Label htmlFor="kb-notes" className="text-xs mb-1 block">Notes for the version log (optional)</Label>
                <Textarea
                  id="kb-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Added CA-125 tumour markers per Dr. Sharma review, refined HbA1c ref ranges."
                  maxLength={500}
                  rows={2}
                  data-testid="kb-notes-input"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={apply}
                  disabled={applyBusy || !validation.ok}
                  className="bg-efgh-orange hover:bg-efgh-orange/90 text-white"
                  data-testid="kb-apply-btn"
                >
                  {applyBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                  Apply & activate
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-border/60 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-ink/50">Want to undo all doctor-uploaded edits?</span>
        <Button variant="outline" onClick={restore} className="text-xs" data-testid="kb-restore-default-btn">
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Restore built-in defaults
        </Button>
      </div>
    </Card>
  );
}

function DiffSection({ label, codes, color }) {
  if (codes.length === 0) return null;
  return (
    <div className="mt-2">
      <div className={`text-xs font-semibold ${color} mb-1`}>{label} ({codes.length})</div>
      <div className="flex flex-wrap gap-1">
        {codes.slice(0, 20).map((c) => (
          <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-white border border-border font-mono">{c}</span>
        ))}
        {codes.length > 20 && <span className="text-xs text-ink/50 py-0.5">+{codes.length - 20} more</span>}
      </div>
    </div>
  );
}

function DiffPanel({ diff }) {
  const nothing = diff.added.length === 0 && diff.removed.length === 0 && diff.modified.length === 0;
  return (
    <div data-testid="kb-diff-panel">
      {nothing ? (
        <p className="text-xs text-emerald-700 italic">No changes vs current active KB.</p>
      ) : (
        <>
          <DiffSection label="Added" codes={diff.added} color="text-emerald-700" />
          <DiffSection label="Removed" codes={diff.removed} color="text-red-700" />
          <DiffSection label="Modified" codes={diff.modified} color="text-amber-700" />
        </>
      )}
    </div>
  );
}

/* ─────────────────────── Version history ─────────────────────── */

function VersionHistory({ versions, loading, onRevert }) {
  if (loading) return <Card className="p-10 text-center text-ink/50 rounded-2xl">Loading history…</Card>;
  if (versions.length === 0) return <Card className="p-8 text-center text-ink/50 rounded-2xl text-sm">No uploads yet — you&apos;re on the built-in default KB.</Card>;
  return (
    <Card className="p-5 rounded-2xl" data-testid="kb-history-card">
      <div className="flex items-center gap-2 mb-4">
        <History className="w-5 h-5 text-efgh-orange" />
        <h2 className="font-heading font-semibold text-xl text-ink">Version history</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-ink/50 border-b border-border">
            <tr>
              <th className="py-2 pr-3">Version</th>
              <th className="py-2 pr-3">When</th>
              <th className="py-2 pr-3">By</th>
              <th className="py-2 pr-3">Markers</th>
              <th className="py-2 pr-3">Changes</th>
              <th className="py-2 pr-3">Notes</th>
              <th className="py-2 pr-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.id} className={`border-b border-border/60 ${v.is_active ? "bg-emerald-50/40" : ""}`} data-testid={`kb-version-row-${v.version_number}`}>
                <td className="py-2 pr-3">
                  <span className="font-mono font-semibold">v{v.version_number}</span>
                  {v.is_active && <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">active</span>}
                  {v.reverted_from && <span className="ml-1 text-xs text-ink/40">↺ revert</span>}
                </td>
                <td className="py-2 pr-3 text-ink/70 whitespace-nowrap">{fmtDate(v.uploaded_at)}</td>
                <td className="py-2 pr-3 text-ink/70">{v.uploaded_by}</td>
                <td className="py-2 pr-3">{v.biomarker_count}</td>
                <td className="py-2 pr-3 text-xs">
                  {(v.added_codes?.length || 0) > 0 && <span className="text-emerald-700 mr-2">+{v.added_codes.length}</span>}
                  {(v.removed_codes?.length || 0) > 0 && <span className="text-red-700 mr-2">-{v.removed_codes.length}</span>}
                  {(v.modified_codes?.length || 0) > 0 && <span className="text-amber-700">~{v.modified_codes.length}</span>}
                </td>
                <td className="py-2 pr-3 text-xs text-ink/70 max-w-[220px] truncate" title={v.notes}>{v.notes || "—"}</td>
                <td className="py-2 pr-3 text-right">
                  {!v.is_active && (
                    <Button variant="outline" size="sm" onClick={() => onRevert(v)} data-testid={`kb-revert-btn-${v.version_number}`}>
                      <RotateCcw className="w-3 h-3 mr-1" /> Revert
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ─────────────────────── Page ─────────────────────── */

export default function AdminKnowledgeBase() {
  const [meta, setMeta] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(true);

  const load = useCallback(async () => {
    try {
      const [m, v] = await Promise.all([
        api.get("/admin/knowledge-base/current"),
        api.get("/admin/knowledge-base/versions"),
      ]);
      setMeta(m.data);
      setVersions(v.data.versions || []);
    } catch (e) { toast.error(apiError(e, "Could not load knowledge base")); }
    finally { setLoadingVersions(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async (fmt) => {
    try {
      const url = `${API_BASE}/admin/knowledge-base/download?fmt=${fmt}`;
      const ts = new Date().toISOString().slice(0, 10);
      await downloadBlob(url, `efgh-kb-${ts}.${fmt}`);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error(apiError(e, "Download failed")); }
  };

  const handleRevert = async (v) => {
    if (!window.confirm(`Revert to version v${v.version_number}? A new version will be created pointing at this snapshot (audit trail preserved).`)) return;
    try {
      const r = await api.post(`/admin/knowledge-base/revert/${v.id}`);
      toast.success(`Reverted · new active version v${r.data.version.version_number}`);
      load();
    } catch (e) { toast.error(apiError(e, "Revert failed")); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-5 pb-8" data-testid="admin-knowledge-base-page">
        <div>
          <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-efgh-orange" /> Knowledge base
          </h1>
          <p className="text-ink/60 text-base">
            Manage every biomarker the Smart Report engine understands. Download, hand off to your clinical panel, and upload the reviewed version back.
          </p>
        </div>

        <CurrentKbCard meta={meta} onDownload={handleDownload} />

        <UploadCard
          onValidated={() => {}}
          onApplied={load}
          onRestore={load}
        />

        <VersionHistory
          versions={versions}
          loading={loadingVersions}
          onRevert={handleRevert}
        />
      </div>
    </DashboardLayout>
  );
}
